import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useTier } from '../hooks/useTier';
import { db } from '../config/firebase';
import { collection, getDocs, query, where, doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import Skeleton from './Skeleton';

export default function NotesManager() {
  const { user } = useAuth();
  const toast = useToast();
  const { isPremium } = useTier();
  const [meetings, setMeetings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [showArchived, setShowArchived] = useState(false);


  useEffect(() => {
    if (user) {
      loadMeetingsWithNotes();
    }
  }, [user]);

  useEffect(() => {
    if (selectedMeeting) {
      loadNotesForMeeting(selectedMeeting);
    }
  }, [showArchived]);

  const loadMeetingsWithNotes = async () => {
    setIsLoading(true);
    try {

      // Strategy: Check both meeting history AND meetings the user created
      const meetingIds = new Set();
      const meetingDataMap = new Map();

      // 1. Get meetings from user's meeting history
      try {
        const historyRef = collection(db, 'users', user.uid, 'meetingHistory');
        const historySnapshot = await getDocs(historyRef);

        historySnapshot.docs.forEach(doc => {
          meetingIds.add(doc.id);
          meetingDataMap.set(doc.id, {
            createdAt: doc.data().joinedAt || doc.data().createdAt,
            template: doc.data().template,
            meetingName: doc.data().meetingName,
            createdByEmail: doc.data().createdByEmail || 'Unknown'
          });
        });
      } catch (historyError) {
      }

      // 2. Get meetings created by the user
      try {
        const meetingsRef = collection(db, 'meetings');
        const userMeetingsQuery = query(meetingsRef, where('createdBy', '==', user.uid));
        const userMeetingsSnapshot = await getDocs(userMeetingsQuery);

        userMeetingsSnapshot.docs.forEach(doc => {
          if (!meetingIds.has(doc.id)) {
            meetingIds.add(doc.id);
            meetingDataMap.set(doc.id, {
              createdAt: doc.data().createdAt,
              template: doc.data().template,
              meetingName: doc.data().meetingName || `Meeting ${doc.id.slice(0, 8)}`,
              createdByEmail: doc.data().createdByEmail || 'Unknown'
            });
          }
        });
      } catch (meetingsError) {
      }

      // Check each meeting for notes IN PARALLEL for faster loading
      const noteCheckPromises = Array.from(meetingIds).map(async (meetingId) => {
        try {
          const notesRef = collection(db, 'meetings', meetingId, 'notes');
          const notesSnapshot = await getDocs(notesRef);

          if (notesSnapshot.size > 0) {
            // Check if user has any notes in this meeting (private or shared)
            const userNotes = notesSnapshot.docs.filter(noteDoc => {
              const note = noteDoc.data();
              // Show if: shared note OR private note that user owns
              // Don't filter archived here - we'll handle that in the meeting list
              return !note.isPrivate || note.userId === user.uid;
            });

            if (userNotes.length > 0) {
              const meetingData = meetingDataMap.get(meetingId) || {};
              return {
                id: meetingId,
                notesCount: userNotes.length,
                createdAt: meetingData.createdAt,
                template: meetingData.template,
                meetingName: meetingData.meetingName || `Meeting ${meetingId.slice(0, 8)}`,
                createdByEmail: meetingData.createdByEmail || 'Unknown'
              };
            }
          }
          return null;
        } catch (noteError) {
          return null;
        }
      });

      const results = await Promise.all(noteCheckPromises);
      const meetingsWithNotes = results.filter(meeting => meeting !== null);


      // Sort by creation date (newest first)
      meetingsWithNotes.sort((a, b) => {
        const dateA = a.createdAt?.toMillis?.() || 0;
        const dateB = b.createdAt?.toMillis?.() || 0;
        return dateB - dateA;
      });

      setMeetings(meetingsWithNotes);
    } catch (error) {
      toast.error('Failed to load notes');
    } finally {
      setIsLoading(false);
    }
  };

  const loadNotesForMeeting = async (meetingId) => {
    setLoadingNotes(true);
    try {
      const notesRef = collection(db, 'meetings', meetingId, 'notes');
      const snapshot = await getDocs(notesRef);

      const meetingNotes = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(note => {
          // Show if: shared note OR private note that user owns
          const hasAccess = !note.isPrivate || note.userId === user.uid;

          // Filter by archived state
          if (showArchived) {
            return hasAccess && note.archived;
          } else {
            return hasAccess && !note.archived;
          }
        })
        .sort((a, b) => a.timestamp - b.timestamp);

      setNotes(meetingNotes);
    } catch (error) {
      toast.error('Failed to load notes for this meeting');
    } finally {
      setLoadingNotes(false);
    }
  };

  const archiveNote = async (meetingId, noteId) => {
    try {
      const noteRef = doc(db, 'meetings', meetingId, 'notes', noteId);
      await updateDoc(noteRef, {
        archived: true,
        archivedAt: serverTimestamp(),
        archivedBy: user.uid
      });

      toast.success('Note archived. It will be deleted in 30 days.');

      setNotes(prev => prev.filter(note => note.id !== noteId));

      const meeting = meetings.find(m => m.id === meetingId);
      if (meeting) {
        const newCount = meeting.notesCount - 1;
        if (newCount === 0) {
          setMeetings(prev => prev.filter(m => m.id !== meetingId));
          setSelectedMeeting(null);
        } else {
          setMeetings(prev => prev.map(m =>
            m.id === meetingId ? { ...m, notesCount: newCount } : m
          ));
        }
      }
    } catch (error) {
      toast.error('Failed to archive note');
    }
  };

  const deleteNote = async (meetingId, noteId) => {
    if (!window.confirm('Are you sure you want to permanently delete this note? This cannot be undone.')) {
      return;
    }

    try {
      const noteRef = doc(db, 'meetings', meetingId, 'notes', noteId);
      await deleteDoc(noteRef);

      toast.success('Note deleted permanently');

      setNotes(prev => prev.filter(note => note.id !== noteId));

      const meeting = meetings.find(m => m.id === meetingId);
      if (meeting) {
        const newCount = meeting.notesCount - 1;
        if (newCount === 0) {
          setMeetings(prev => prev.filter(m => m.id !== meetingId));
          setSelectedMeeting(null);
        } else {
          setMeetings(prev => prev.map(m =>
            m.id === meetingId ? { ...m, notesCount: newCount } : m
          ));
        }
      }
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const unarchiveNote = async (meetingId, noteId) => {
    try {
      const noteRef = doc(db, 'meetings', meetingId, 'notes', noteId);
      await updateDoc(noteRef, {
        archived: false,
        archivedAt: null,
        archivedBy: null
      });

      toast.success('Note restored from archive');

      setNotes(prev => prev.filter(note => note.id !== noteId));
    } catch (error) {
      toast.error('Failed to unarchive note');
    }
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const exportNotes = (meetingId) => {
    const meeting = meetings.find(m => m.id === meetingId);
    const meetingNotes = notes;

    const content = `# Meeting Notes - ${meeting.template?.name || 'QuickMeet Meeting'}
Meeting ID: ${meetingId}
Date: ${meeting.createdAt ? new Date(meeting.createdAt.toMillis()).toLocaleString() : 'Unknown'}
Organizer: ${meeting.createdByEmail || 'Unknown'}

---

${meetingNotes.map((note, index) => `
## Note ${index + 1} - ${formatDuration(note.meetingDuration)} into meeting
**Time:** ${new Date(note.timestamp).toLocaleTimeString()}
**Author:** ${note.userName}
**Visibility:** ${note.isPrivate ? 'Private' : 'Shared'}

${note.content}

---
`).join('\n')}

Generated with QuickMeet - ${new Date().toLocaleString()}
`;

    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `meeting-notes-${meetingId}.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success('Notes exported successfully!');
  };

  if (!isPremium) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="bg-gradient-to-r from-purple-900/30 to-indigo-900/30 border-2 border-purple-700/50 rounded-2xl p-8 text-center">
          <div className="w-20 h-20 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Upgrade to Pro for Meeting Notes</h2>
          <p className="text-white/80 mb-6">
            Create timestamped notes during meetings, share with your team, and export for future reference.
          </p>
          <button className="px-8 py-3 bg-gradient-to-r from-[#06c] to-[#0055d4] text-white font-semibold rounded-lg hover:shadow-xl transition-all">
            Upgrade to Pro
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-[#1d1d1f] dark:text-white mb-2">Notes</h1>
        <p className="text-[#86868b] dark:text-[#8e8e93]">
          View and export notes from your meetings
        </p>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} />
          ))}
        </div>
      ) : meetings.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold text-[#1d1d1f] dark:text-white mb-2">No notes yet</h2>
          <p className="text-[#86868b] dark:text-[#8e8e93]">
            Start a meeting and take timestamped notes to see them here
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Meetings List */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-[#1d1d1f] dark:text-white mb-4">
              Meetings with Notes ({meetings.length})
            </h2>
            <div className="space-y-3">
              {meetings.map(meeting => (
                <button
                  key={meeting.id}
                  onClick={() => {
                    setSelectedMeeting(meeting.id);
                    loadNotesForMeeting(meeting.id);
                  }}
                  className={`w-full text-left p-5 rounded-xl transition-all ${
                    selectedMeeting === meeting.id
                      ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg'
                      : 'bg-white dark:bg-[#1c1c1e] hover:bg-[#f5f5f7] dark:hover:bg-[#2c2c2e] border border-[#d2d2d7] dark:border-[#3c3c3e]'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      {meeting.template?.icon && (
                        <div className="text-3xl">{meeting.template.icon}</div>
                      )}
                      <div>
                        <h3 className={`font-semibold ${selectedMeeting === meeting.id ? 'text-white' : 'text-[#1d1d1f] dark:text-white'}`}>
                          {meeting.template?.name || 'Meeting'}
                        </h3>
                        <p className={`text-sm ${selectedMeeting === meeting.id ? 'text-white/80' : 'text-[#86868b] dark:text-[#8e8e93]'}`}>
                          {meeting.createdAt && new Date(meeting.createdAt.toMillis()).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                      selectedMeeting === meeting.id
                        ? 'bg-white/20 text-white'
                        : 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
                    }`}>
                      {meeting.notesCount} {meeting.notesCount === 1 ? 'note' : 'notes'}
                    </div>
                  </div>
                  <p className={`text-xs ${selectedMeeting === meeting.id ? 'text-white/70' : 'text-[#86868b] dark:text-[#8e8e93]'}`}>
                    Organized by {meeting.createdByEmail}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Notes Display */}
          <div className="bg-white dark:bg-[#1c1c1e] rounded-xl p-6 border border-[#d2d2d7] dark:border-[#3c3c3e] min-h-[600px]">
            {!selectedMeeting ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="w-16 h-16 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <p className="text-[#86868b] dark:text-[#8e8e93]">
                  Select a meeting to view its notes
                </p>
              </div>
            ) : loadingNotes ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} />
                ))}
              </div>
            ) : (
              <>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
                  <h3 className="text-xl font-semibold text-[#1d1d1f] dark:text-white">
                    {showArchived ? 'Archived Notes' : 'Notes'} ({notes.length})
                  </h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowArchived(!showArchived)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                        showArchived
                          ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 hover:bg-orange-200 dark:hover:bg-orange-900/50'
                          : 'bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white hover:bg-[#e8e8ed] dark:hover:bg-[#3c3c3e]'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                      </svg>
                      {showArchived ? 'Show Active' : 'Show Archived'}
                    </button>
                    {!showArchived && (
                      <button
                        onClick={() => exportNotes(selectedMeeting)}
                        className="px-4 py-2 bg-[#06c] text-white rounded-lg text-sm font-medium hover:bg-[#0077ed] transition-all flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Export
                      </button>
                    )}
                  </div>
                </div>

                <div className="space-y-4 max-h-[500px] overflow-y-auto custom-scrollbar">
                  {notes.map((note) => (
                    <div
                      key={note.id}
                      className="p-4 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-lg border border-[#d2d2d7] dark:border-[#3c3c3e]"
                    >
                      {/* Note Header */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 text-xs text-[#86868b] dark:text-[#8e8e93]">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="font-medium">{formatDuration(note.meetingDuration)}</span>
                          <span>•</span>
                          <span>{new Date(note.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        {note.isPrivate ? (
                          <div className="flex items-center gap-1 px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 border border-purple-300 dark:border-purple-700/50 rounded-full">
                            <svg className="w-3 h-3 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                            <span className="text-purple-600 dark:text-purple-400 text-xs font-medium">Private</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 px-2 py-0.5 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700/50 rounded-full">
                            <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            <span className="text-green-600 dark:text-green-400 text-xs font-medium">Shared</span>
                          </div>
                        )}
                      </div>

                      {/* Note Author */}
                      <div className="text-[#86868b] dark:text-[#8e8e93] text-xs font-medium mb-2 flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 flex items-center justify-center text-white text-[10px] font-bold">
                          {note.userName?.[0]?.toUpperCase() || 'G'}
                        </div>
                        {note.userName || 'Guest'}
                        {note.userId === user.uid && <span className="text-[#06c]">(You)</span>}
                      </div>

                      {/* Note Content */}
                      <p className="text-[#1d1d1f] dark:text-white text-sm leading-relaxed whitespace-pre-wrap mb-3">
                        {note.content}
                      </p>

                      {/* Archived Info */}
                      {note.archived && note.archivedAt && (
                        <div className="flex items-center gap-2 text-xs text-orange-600 dark:text-orange-400 mt-2 p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>
                            Archived on {new Date(note.archivedAt.toMillis()).toLocaleDateString()} - Will be deleted in 30 days
                          </span>
                        </div>
                      )}

                      {/* Note Actions (only show for user's own notes) */}
                      {note.userId === user.uid && (
                        <div className="flex items-center gap-2 pt-2 border-t border-[#d2d2d7] dark:border-[#3c3c3e] mt-3">
                          {note.archived ? (
                            <button
                              onClick={() => unarchiveNote(selectedMeeting, note.id)}
                              className="flex items-center gap-1 px-3 py-1.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg text-xs font-medium hover:bg-green-200 dark:hover:bg-green-900/50 transition-all"
                              title="Restore this note from archive"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                              Unarchive
                            </button>
                          ) : (
                            <button
                              onClick={() => archiveNote(selectedMeeting, note.id)}
                              className="flex items-center gap-1 px-3 py-1.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 rounded-lg text-xs font-medium hover:bg-orange-200 dark:hover:bg-orange-900/50 transition-all"
                              title="Archive this note (deletes after 30 days)"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                              </svg>
                              Archive
                            </button>
                          )}
                          <button
                            onClick={() => deleteNote(selectedMeeting, note.id)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg text-xs font-medium hover:bg-red-200 dark:hover:bg-red-900/50 transition-all"
                            title="Delete this note permanently"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
